import { usePagedDirectoryDataSource } from 'custom_hooks/data_hooks/data_source/directoryDataSource';
import { FileDetailsPage } from 'pages/Settings/FileLibrary/FileDetails/FileDetailsPage';
import { useEffect } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import {
    ProtectedCorporationContent,
    ProtectedLocationContent,
} from 'routing/EntityContent';
import { PremiumContent } from 'routing/PremiumContent';
import { FileLibraryListView } from './FileLibraryListView';
import { useFileFromLibrary } from './hooks/useFileFromLibrary';
import { useFolderIdsInPath } from './hooks/useFolderIdsInPath';
import { FileLibraryProps } from './types';

export function FileLibraryPage({ location, corporation }: FileLibraryProps) {
    const { url } = useRouteMatch();
    const routeMatch = useRouteMatch<{ directoryId?: string | undefined }>(
        '*/d/:directoryId',
    );
    const fileRouteMatch = useRouteMatch<{ fileId?: string | undefined }>('*/f/:fileId');

    const corporationId = corporation?.Id;
    const locationId = location?.Id;

    const {
        data: file,
        isLoading: isFileLoading,
        refetch: refetchFile,
    } = useFileFromLibrary(fileRouteMatch?.params.fileId ?? null);

    const { data: enrichedPathFragments } = useFolderIdsInPath({
        corporationId,
        locationId,
        path: '/',
        id: file?.Id ?? routeMatch?.params.directoryId,
    });

    const dataSource = usePagedDirectoryDataSource({
        name: 'file-library-table',
        defaultFilter: {
            nameSearch: '',
            corporationId,
            locationId,
            folderPath: '/',
            folderId: routeMatch?.params.directoryId,
        },
        keepPreviousData: true,
    });

    const { setPage } = dataSource;

    useEffect(() => {
        setPage(1);
    }, [routeMatch?.params.directoryId, fileRouteMatch?.params.fileId, setPage]);

    return (
        <Switch>
            <Route
                path={`${url}/f/:fileId`}
                exact
                render={() => (
                    <PremiumContent allowSaas>
                        <ProtectedCorporationContent
                            pageName="corporation-file-details"
                            render={() => (
                                <FileDetailsPage
                                    file={file}
                                    isLoading={isFileLoading}
                                    refetch={refetchFile}
                                    refreshTable={dataSource.reloadPage}
                                    folderHierarchy={enrichedPathFragments}
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${url}/f/:fileId`}
                exact
                render={() => (
                    <PremiumContent allowSaas>
                        <ProtectedLocationContent
                            pageName="location-file-details"
                            render={() => (
                                <FileDetailsPage
                                    file={file}
                                    isLoading={isFileLoading}
                                    refetch={refetchFile}
                                    refreshTable={dataSource.reloadPage}
                                    folderHierarchy={enrichedPathFragments}
                                />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            {/* @ts-expect-error: type issue with older version of react-router-dom */}
            <Route default>
                <FileLibraryListView
                    corporation={corporation}
                    location={location}
                    dataSource={dataSource}
                    path={enrichedPathFragments ?? []}
                />
            </Route>
            <Route path={`${url}/d/:directoryId`}>
                <FileLibraryListView
                    corporation={corporation}
                    location={location}
                    dataSource={dataSource}
                    path={enrichedPathFragments ?? []}
                />
            </Route>
        </Switch>
    );
}
